// Host side of a Bitreel embed: one iframe, its postMessage events under one
// prefix, the ready handshake and per-frame config updates. The digital
// print viewer and editor follow CLIENT_INTEGRATION.md in
// bitreel-fiber-product-viewer.

const REQUEST_TIMEOUT_MS = 15000;

let requestCounter = 0;

export class BitreelFrameClient {
  constructor({ url, prefix }) {
    this.url = url;
    this.prefix = prefix;
    this.origin = new URL(url, window.location.href).origin;
    this.iframe = null;
    // idle | loading | ready
    this.status = 'idle';
    this.version = null;
    this.capabilities = [];
    this.lastError = null;
    // Everything sent so far, resent whole if the embed reloads.
    this._state = {};
    this._pending = null;
    this._flushHandle = 0;
    this._requests = new Map();
    this._handlers = new Map();
    this._listeners = new Set();
    this._onMessage = this._onMessage.bind(this);
  }

  get attached() {
    return !!this.iframe;
  }

  // Pins the embed to this page: it then only takes commands from, and
  // replies to, this origin.
  get src() {
    const src = new URL(this.url, window.location.href);
    src.searchParams.set('origin', window.location.origin);
    return src.href;
  }

  type(name) {
    return `${this.prefix}-${name}`;
  }

  supports(capability) {
    return this.capabilities.includes(capability);
  }

  // Status changes: ready, reloads and errors.
  onChange(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  // One handler per incoming event name, given the event's detail.
  on(name, handler) {
    this._handlers.set(this.type(name), handler);
    return () => this._handlers.delete(this.type(name));
  }

  // Listener before src: the embed posts ready as soon as it starts.
  attach(iframe) {
    if (this.iframe) return;
    this.iframe = iframe;
    this.status = 'loading';
    window.addEventListener('message', this._onMessage);
    iframe.src = this.src;
  }

  // Merges a partial config. Sent once the embed is ready, and at most once
  // per frame while a gesture streams placements.
  setConfig(partial) {
    this._state = { ...this._state, ...partial };
    this._pending = { ...(this._pending || {}), ...partial };
    if (this.status !== 'ready' || this._flushHandle) return;
    this._flushHandle = window.requestAnimationFrame(() => {
      this._flushHandle = 0;
      this._flush();
    });
  }

  send(name, detail) {
    if (!this.iframe?.contentWindow) return;
    this.iframe.contentWindow.postMessage(
      { type: this.type(name), detail },
      this.origin
    );
  }

  // Sends the named event with a requestId and resolves with the detail of
  // the reply event, or rejects with the detail of the failure event.
  request(name, detail, { reply, failure }) {
    if (this.status !== 'ready') {
      return Promise.reject(new Error(`${this.prefix} is not ready`));
    }

    const requestId = `walter-${++requestCounter}`;
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this._requests.delete(requestId);
        reject(new Error(`${this.prefix} ${name} timed out`));
      }, REQUEST_TIMEOUT_MS);
      this._requests.set(requestId, {
        resolve,
        reject,
        timer,
        reply: this.type(reply),
        failure: this.type(failure),
      });
      this.send(name, { ...detail, requestId });
    });
  }

  // The embed's canvas as it is now, as a Blob.
  async capture(options) {
    const detail = await this.request('capture-image', options, {
      reply: 'image-captured',
      failure: 'capture-failed',
    });
    return detail.blob;
  }

  dispose() {
    window.removeEventListener('message', this._onMessage);
    if (this._flushHandle) window.cancelAnimationFrame(this._flushHandle);
    this._flushHandle = 0;
    this._requests.forEach(({ reject, timer }) => {
      window.clearTimeout(timer);
      reject(new Error(`${this.prefix} was removed`));
    });
    this._requests.clear();
    this._handlers.clear();
    this._listeners.clear();
    this.iframe = null;
    this.status = 'idle';
  }

  _notify() {
    this._listeners.forEach(listener => listener(this));
  }

  _flush() {
    if (!this._pending || this.status !== 'ready') return;
    const config = this._pending;
    this._pending = null;
    this.send('set-config', config);
  }

  _settleRequest(type, detail) {
    const pending = this._requests.get(detail?.requestId);
    if (!pending) return false;
    if (type !== pending.reply && type !== pending.failure) return false;

    this._requests.delete(detail.requestId);
    window.clearTimeout(pending.timer);
    if (type === pending.reply) pending.resolve(detail);
    else pending.reject(new Error(detail?.error || `${type}`));
    return true;
  }

  _onMessage(event) {
    if (event.origin !== this.origin) return;
    if (!this.iframe || event.source !== this.iframe.contentWindow) return;

    const { type, detail } = event.data || {};
    if (typeof type !== 'string' || !type.startsWith(`${this.prefix}-`)) {
      return;
    }

    if (type === this.type('ready')) {
      // Also fires again if the embed reloads, so the whole state goes again.
      this.version = detail?.version || null;
      this.capabilities = Array.isArray(detail?.capabilities)
        ? detail.capabilities
        : [];
      this.status = 'ready';
      this._pending = { ...this._state };
      this._flush();
      this._notify();
    } else if (type === this.type('error')) {
      this.lastError = detail || null;
      this._notify();
    } else if (this._settleRequest(type, detail)) {
      return;
    }

    const handler = this._handlers.get(type);
    if (handler) handler(detail);
  }
}
